import {Component, NgZone, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {NotificationService} from "../../../root/toast/notification.service";
import {ModalService} from "../../../root/modal/modal.service";
import {LoadingWheelService} from "../../../root/loading/loading-wheel.service";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {SessionService} from "../../../core/session.service";
import {ErrorMessage} from "../../../shared/sara-input/error-message";
import {CarTemplateService} from "../car-template.service";
import {CarTemplate} from "../model/car-template";
import {COMPANY_URL, CAR_TEMPLATE_URL, APPROVAL_STATUS} from "../constants/car-template-constants";
import {PERM_CAR_TEMPLATE_CREATE, PERM_CAR_TEMPLATE_EDIT} from "../permissions/car-template-permissions";

@Component({
    selector: 'car-template-edit',
    templateUrl: './car-template-edit.component.html',
    providers: [CarTemplateService]
})
export class CarTemplateEditComponent implements OnInit {

    PICTURE_SIZE_LIMIT_MB = 1.5;
    PICTURE_MIN_WIDTH = 200;
    PICTURE_MIN_HEIGHT = 100;
    PICTURE_MAX_WIDTH = 1280;
    PICTURE_MAX_HEIGHT = 720;
    MODEL_MAX_LENGTH = 50;
    BRAND_MAX_LENGTH = 20;

    CANCEL_ALERT_TITLE = "CANCEL_ALERT_TITLE";
    CANCEL_ALERT_DESCRIPTION = "CANCEL_ALERT_DESCRIPTION";

    carTemplate:CarTemplate = new CarTemplate();
    carTemplateForm:FormGroup;
    companyOid: number;
    id: string;
    file:any;
    submitted=false;
    isNew=true;
    isPictureEditable:boolean=false;
    showInvalidPictureMessage:boolean=false;
    fileTooBig:boolean = false;
    fileResolutionWrong:boolean = false;
    fileOrientationWrong:boolean = false;
    auxPictureRequired:boolean = false;

    /*Form collections*/
    categories=[];
    types=[];
    transmisions=["M","A"];
    doors = [2, 3, 4, 5];
    passengers = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
    baggage = [0, 1, 2, 3, 4, 5, 6];

    errorMessages = {
        'required': 'common.error.REQUIRED',
        'required_files': 'ERROR.required.fields',
        'fileTooBig': 'ERROR.carTemplates.big',
        'fileResolutionWrong': 'ERROR.carTemplates.wrongResolution',
        'fileOrientationWrong': 'ERROR.carTemplates.orientation',
        'auxPictureRequired': 'ERROR.carTemplates.auxPictureRequired',
        'errorOnUpdatingAuxPicture': 'ERROR.carTemplates.auxPictureUpdate',
        'error_save':'carTemplate.error.ERROR_SAVE',
        'error_update': 'carTemplate.error.ERROR_UPDATE',
    };

    confirmationMessages = {
        'success_save': 'carTemplate.confirmationMessage.SUCCESS_SAVE',
        'success_update': 'carTemplate.confirmationMessage.SUCCESS_UPDATE',
    };

    constructor(private carTemplateService:CarTemplateService,
                private route: ActivatedRoute,
                private loading:LoadingWheelService,
                private navigation:Router,
                private session:SessionService,
                private formBuilder: FormBuilder,
                private notificationService:NotificationService,
                private modalService:ModalService,
                private  zone: NgZone) {}

    ngOnInit() {

        this.buildCarTemplateForm();
        this.loadRequiredCollections();
        this.route.params.subscribe(params => {
            this.companyOid = params['companyId'];
            this.id = params['id'];
            if (this.id && this.companyOid){
              this.loading.show();
              this.zone.run(() => {
                    this.carTemplateService.getById(this.companyOid, this.id).subscribe(result => {
                        this.bindCarTemplateForm(result);
                        this.isPictureEditable = this.canEditPicture(result);
                        this.showInvalidPictureMessage = this.invalidPictureMessageIsRequired(result);
                        this.isNew=false;
                        this.loading.hide();
                    })
                });
            }
        });
    }

    save(){
         this.submitted = true;
         this.loading.show();
         if (this.carTemplateForm.valid){
             this.zone.run(() => {
                this.carTemplate = this.carTemplateForm.value as CarTemplate;
                if(this.isNew){
                    this.carTemplateService.save(this.carTemplate,this.companyOid).subscribe((response) => {
                        this.persistCallback('carTemplate.confirmationMessageTitle.SUCCESS_SAVE', 'success_save');
                    },
                    (error) => { this.handleError(error, 'error_save');});
                } else{
                    this.carTemplateService.update(this.carTemplate,this.companyOid).subscribe((response) => {
                        this.persistCallback('carTemplate.confirmationMessageTitle.SUCCESS_SAVE', 'success_update');
                    }, (error) => { this.handleError(error, 'error_update'); });
                }
              });
        } else {
            this.loading.hide();
            this.notificationService.notifyErrorByCode(this.errorMessages['required_files']);
        }
    }

    cancel(){
         this.modalService.show(this.CANCEL_ALERT_TITLE, this.CANCEL_ALERT_DESCRIPTION,
             () => this.navigation.navigateByUrl(COMPANY_URL + this.companyOid + CAR_TEMPLATE_URL));
    }

     // on file select event
    onFileChange(htmlEvent:any, files: FileList) {

         const fileReader: FileReader = new FileReader;
         this.fileTooBig = false;
         this.fileResolutionWrong = false;
         this.fileOrientationWrong = false;
         this.zone.run(() => {
             fileReader.onloadend = (event) => {

                if (event.target.readyState == FileReader.DONE) {

                    var img = new Image;

                    img.onload = () => {

                      if (this.isFileResolutionWrong(img)) {
                        this.fileResolutionWrong = true;
                        htmlEvent.target.value = '';
                        return;
                      } else if (this.isFileOrientationWrong(img)) {
                        this.fileOrientationWrong = true;
                        htmlEvent.target.value = '';
                        return;
                      }

                      let picture = {
                          fileContent: fileReader.result.toString(), // BASE64 of the image
                          fileName: files[0].name,
                          title: files[0].name,
                          url: "",
                          token: ""
                      };

                      this.formControls.carModel['controls'].picture.patchValue(picture);
                      htmlEvent.target.value = '';
                    }
                    img.src = fileReader.result.toString();
                }
            }
            if (this.isFileTooBig(files[0].size)) {
                this.fileTooBig = true;
                htmlEvent.target.value = '';
             } else {
                fileReader.readAsDataURL(files[0]);
            }
        });
    }

    updatePicture(){
             this.submitted = true;
             this.loading.show();
             if (this.carTemplateForm.valid) {

             if (this.auxPictureIsValid(this.carTemplateForm.value)) {
                    this.zone.run(() => {
                          this.carTemplate = this.carTemplateForm.value as CarTemplate;
                          this.carTemplateService.updatePicture(this.carTemplate,this.companyOid).subscribe((response) => {
                              this.persistCallback('carTemplate.confirmationMessageTitle.SUCCESS_SAVE', 'success_update');
                          }, (error) => {
                                 this.loading.hide();
                                 this.notificationService.notifyErrorByCode(this.errorMessages['errorOnUpdatingAuxPicture']);
                           });
                    });
              }
              else {
                  this.loading.hide();
                  this.notificationService.notifyErrorByCode(this.errorMessages['auxPictureRequired']);
               }

            } else {
                this.loading.hide();
                this.notificationService.notifyErrorByCode(this.errorMessages['required_files']);
            }
    }

    onAuxFileChange(htmlEvent:any, files: FileList) {

         const fileReader: FileReader = new FileReader;
         this.fileTooBig = false;
         this.fileResolutionWrong = false;
         this.fileOrientationWrong = false;
         this.zone.run(() => {
             fileReader.onloadend = (event) => {

                if (event.target.readyState == FileReader.DONE) {

                    var img = new Image;

                    img.onload = () => {

                      if (this.isFileResolutionWrong(img)) {
                        this.fileResolutionWrong = true;
                        htmlEvent.target.value = '';
                        return;
                      } else if (this.isFileOrientationWrong(img)) {
                        this.fileOrientationWrong = true;
                        htmlEvent.target.value = '';
                        return;
                      }

                      let auxpicture = {
                          fileContent: fileReader.result.toString(), // BASE64 of the image
                          fileName: files[0].name,
                          title: files[0].name,
                          url: "",
                          token: ""
                      };

                      this.formControls.carModel['controls'].auxPicture.patchValue(auxpicture);
                      htmlEvent.target.value = '';
                    }
                    img.src = fileReader.result.toString();
                }
            }
            if (this.isFileTooBig(files[0].size)) {
                this.fileTooBig = true;
                htmlEvent.target.value = '';
             } else {
                fileReader.readAsDataURL(files[0]);
            }
        });
    }

    // Getter method to access formcontrols
     get formControls() {
       return this.carTemplateForm.controls;
     }

    identify(index,item){
       return item.id;
    }

    showActionButtons(){
       return this.session.loggedUserAllowed(PERM_CAR_TEMPLATE_EDIT) || this.session.loggedUserAllowed(PERM_CAR_TEMPLATE_CREATE);
    };

    private loadRequiredCollections() {
        this.zone.run(() => {
            this.carTemplateService.getAllCarCategories().subscribe(carCategories => {
                 this.categories = carCategories;
            });
            this.carTemplateService.getAllCarTypes().subscribe(carTypes => {
                 this.types = carTypes;
            });
        });
    }

    private persistCallback(confirmationMesageTitle:string, confirmationMessageCode:string){
        this.loading.hide();
        this.notificationService.displayToast(confirmationMesageTitle,this.confirmationMessages[confirmationMessageCode], null, 'SUCCESS');
        this.navigation.navigateByUrl(COMPANY_URL + this.companyOid + CAR_TEMPLATE_URL);
    }

    private handleError(error, errorMessageCode:string) {
        this.loading.hide();
        if (error.status == 400){
           let codes = JSON.parse(error._body);
           this.notificationService.notifyFrontErrorDetailByCodes(codes);
        }else{
            this.notificationService.notifyErrorByCode(this.errorMessages[errorMessageCode]);
        }
    }

    private buildCarTemplateForm() {
        this.carTemplateForm = this.formBuilder.group({
           oid:[''],
           version:[''],
           passengers:['', Validators.required],
           airConditioning:[false, Validators.required],
           doors:['', Validators.required],
           smallBags:['', Validators.required],
           largeBags:['', Validators.required],
           transmission:['', Validators.required],
           approvalStatus:[''],
           active:[false],
           creationDate:[null],
           createdByCompany:[null],
            carModel : this.formBuilder.group({
              oid:[''],
              version:[''],
              brand: ['', [Validators.required, Validators.maxLength(this.BRAND_MAX_LENGTH)]],
              model: ['', [Validators.required, Validators.maxLength(this.MODEL_MAX_LENGTH)]],
             category: ['', Validators.required],
             type: ['', Validators.required],
             picture: this.formBuilder.group({
                  oid:[''],
                  version:[''],
                  title: [''],
                  fileName: [''],
                  fileContent:[''],
                  token: [''],
                  url: ['']
            }, { validator: this.pictureIsRequired}),
            auxPicture: this.formBuilder.group({
                              oid:[''],
                              version:[''],
                              title: [''],
                              fileName: [''],
                              fileContent:[''],
                              token: [''],
                              url: ['']
            }),
            }),
        });
    }

    private bindCarTemplateForm(carTemplate:CarTemplate){
        this.carTemplateForm.patchValue(carTemplate);
    }

    private pictureIsRequired(group : FormGroup) : {[s:string ]: boolean} {
      if (group) {
        const fileName = group.controls['fileName'].value;
        const fileContent = group.controls['fileContent'].value;
        if((fileContent && fileContent.length > 0) || (fileName && fileName.length > 0)) {
          return null;
        }
      }
      return {pictureRequiredError: true};
    }


    private auxPictureIsValid(carTemplate : CarTemplate) : boolean {
      if (carTemplate.carModel && carTemplate.carModel.auxPicture) {
        const fileName = carTemplate.carModel.auxPicture.fileName;
        const fileContent = carTemplate.carModel.auxPicture.fileContent;
        if(fileContent && fileContent.length > 0) {
          this.auxPictureRequired=false;
          return true;
        }
      }
      this.auxPictureRequired=true;
      return false;
    }

    private isFileOrientationWrong(image: HTMLImageElement) {
        // images must be in landscape orientation, so width greater than height
        return image.width <= image.height;
    }
    private isFileTooBig(fileSizeInBytes: number) {
        return ((fileSizeInBytes / 1000) / 1000) > this.PICTURE_SIZE_LIMIT_MB;
    }

    private isFileResolutionWrong(image: HTMLImageElement) {
        return (image.width < this.PICTURE_MIN_WIDTH || image.height < this.PICTURE_MIN_HEIGHT || image.width > this.PICTURE_MAX_WIDTH || image.height > this.PICTURE_MAX_HEIGHT);
    }

    private canEditPicture(carTemplate:CarTemplate) {
        return (carTemplate.approvalStatus == APPROVAL_STATUS.PENDING_IMAGE_VALIDATION || (carTemplate.approvalStatus == APPROVAL_STATUS.PENDING_IMAGE_UPDATE));
    }

    private invalidPictureMessageIsRequired(carTemplate:CarTemplate) {
        return carTemplate.approvalStatus == APPROVAL_STATUS.PENDING_IMAGE_UPDATE;
    }
}


// WEBPACK FOOTER //
// ./public/upgrade/feature/carTemplate/edit/car-template-edit.component.ts